import React from 'react';
import { Box, Typography, Chip, keyframes } from '@mui/material';
import { PowerOffOutlined } from '@mui/icons-material';
import { formatDecimalMegabytes } from './formatStorageBytes';
import type { MetricDef } from '../Settings/reportDsl';

export interface DamStorageQuotaBarProps {
  usedMb: number;
  maxStorageTb: number;
  folderPath?: string;
  /** Cumulative asset count (incl. subfolders) when the query includes asset_count_cumulative. */
  assetCount?: number;
}

/** Decimal MB per TB (matches AEM ingest: 1 TB = 1_000_000 MB). */
export const decimalMbPerTb = (tb: number): number => tb * 1_000_000;

export const getStorageQuotaPercent = (usedMb: number, maxStorageTb: number): number => {
  const maxMb = decimalMbPerTb(maxStorageTb);
  if (!Number.isFinite(maxMb) || maxMb <= 0) return 0;
  return (usedMb / maxMb) * 100;
};

export const formatMaxStorageTb = (tb: number): string => {
  if (!Number.isFinite(tb) || tb <= 0) return '0 TB';
  // Sub-terabyte quotas read better in GB (decimal: 1 TB = 1000 GB).
  if (tb < 1) {
    const gb = tb * 1000;
    if (Number.isInteger(gb)) return `${gb} GB`;
    return `${gb >= 10 ? gb.toFixed(1) : gb.toFixed(2)} GB`;
  }
  // Petabyte quotas (decimal: 1 PB = 1000 TB).
  if (tb >= 1000) {
    const pb = tb / 1000;
    if (Number.isInteger(pb)) return `${pb} PB`;
    if (pb >= 10) return `${pb.toFixed(1)} PB`;
    return `${pb.toFixed(2)} PB`;
  }
  if (tb >= 100) return `${tb.toFixed(0)} TB`;
  if (tb >= 10) return `${tb.toFixed(1)} TB`;
  return `${tb.toFixed(2)} TB`;
};

/** Solid accent used for folder-path chips so they match the quota bar fill. */
export const getStorageQuotaBarAccentColor = (percent: number): string => {
  if (percent > 100) return '#ef4444';
  if (percent >= 95) return '#dc2626';
  if (percent >= 70) return '#d97706';
  return '#059669';
};

export const getStorageQuotaBarStyle = (
  percent: number,
): { gradient: string; glow: boolean } => {
  if (percent > 100) {
    return {
      gradient: 'linear-gradient(90deg, #b91c1c 0%, #ef4444 50%, #f87171 100%)',
      glow: true,
    };
  }
  if (percent >= 95) {
    return { gradient: 'linear-gradient(90deg, #b91c1c 0%, #ef4444 100%)', glow: false };
  }
  if (percent >= 70) {
    return { gradient: 'linear-gradient(90deg, #d97706 0%, #f59e0b 100%)', glow: false };
  }
  return { gradient: 'linear-gradient(90deg, #059669 0%, #10b981 100%)', glow: false };
};

const quotaGlow = keyframes`
  0%, 100% { box-shadow: 0 0 8px rgba(239, 68, 68, 0.55), 0 0 18px rgba(239, 68, 68, 0.35); }
  50% { box-shadow: 0 0 14px rgba(239, 68, 68, 0.85), 0 0 28px rgba(239, 68, 68, 0.55); }
`;

const parseMetricNumber = (raw: unknown): number | undefined => {
  if (raw === null || raw === undefined || raw === '') return undefined;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) ? n : undefined;
};

/**
 * Read a metric value from ClickHouse rows by field match (`metric_0`, `metric_1`, …).
 * When the query is grouped (e.g. latest snapshot per `site_id` on an all-sites
 * ClickHouse config), values are summed so the bar shows program-wide usage.
 */
export const readQuotaMetricFromRows = (
  rows: Record<string, unknown>[],
  metrics: Array<{ field?: string; label?: string }>,
  fieldSubstring: string,
  fallbackIdx = 0,
): number => {
  if (!rows.length) return 0;
  let total = 0;
  for (const row of rows) {
    const idx = metrics.findIndex((m) => (m.field || '').includes(fieldSubstring));
    const i = idx >= 0 ? idx : fallbackIdx;
    const label = metrics[i]?.label;
    const raw = row[`metric_${i}`] ?? (label ? row[label] : undefined);
    const n = parseMetricNumber(raw);
    if (n !== undefined) total += n;
  }
  return total;
};

export const hasAssetCountMetric = (
  metrics: Array<{ field?: string }>,
): boolean => metrics.some((m) => (m.field || '').includes('asset_count'));

/** Matching snapshot rows in range — `count()` metric; ClickHouse still returns one zero row when empty. */
export const readSnapshotCountFromRows = (
  rows: Record<string, unknown>[],
  metrics: Array<{ fn?: string }>,
): number => {
  if (!rows.length) return 0;
  const countIdx = metrics.findIndex((m) => m.fn === 'count');
  if (countIdx >= 0) {
    let total = 0;
    for (const row of rows) {
      total += parseMetricNumber(row[`metric_${countIdx}`]) ?? 0;
    }
    return total;
  }
  return rows.length;
};

export const QUOTA_NO_STATS_MESSAGE = 'No data available. Please check with administrator';

export const StorageQuotaNoStats: React.FC = () => (
  <Box
    sx={{
      py: 4,
      px: 2,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1.5,
    }}
  >
    <PowerOffOutlined
      sx={{
        fontSize: 52,
        color: 'text.disabled',
        opacity: 0.85,
      }}
    />
    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
      {QUOTA_NO_STATS_MESSAGE}
    </Typography>
  </Box>
);

/** Compact unplugged icon for table cells when a folder has no snapshot data in range. */
export const StorageQuotaCellNoData: React.FC = () => (
  <PowerOffOutlined
    aria-label={QUOTA_NO_STATS_MESSAGE}
    titleAccess={QUOTA_NO_STATS_MESSAGE}
    sx={{
      fontSize: 22,
      color: 'text.disabled',
      opacity: 0.85,
      display: 'block',
    }}
  />
);

/** Ensures a hidden count() metric is present so empty ranges are detectable. */
export const withSnapshotCountMetric = (metrics: MetricDef[]): MetricDef[] => {
  if (metrics.some((m) => m.fn === 'count')) return metrics;
  return [...metrics, { field: '*', fn: 'count', label: '_snapshots' }];
};

export const DamStorageQuotaBar: React.FC<DamStorageQuotaBarProps> = ({
  usedMb,
  maxStorageTb,
  folderPath,
  assetCount,
}) => {
  const percent = getStorageQuotaPercent(usedMb, maxStorageTb);
  const { gradient, glow } = getStorageQuotaBarStyle(percent);
  const barAccent = getStorageQuotaBarAccentColor(percent);
  const fillWidth = Math.min(100, Math.max(0, percent));
  const usedLabel = formatDecimalMegabytes(usedMb);
  const maxLabel = formatMaxStorageTb(maxStorageTb);
  const percentLabel = `${percent >= 100 ? percent.toFixed(1) : percent.toFixed(1)}%`;
  const showAssetCount = typeof assetCount === 'number' && Number.isFinite(assetCount);

  return (
    <Box sx={{ py: 2, px: 0.5 }}>
      {showAssetCount && (
        <Typography variant="h6" fontWeight={700} sx={{ mb: 1.5, textAlign: 'center' }}>
          {Math.round(assetCount).toLocaleString()} assets
        </Typography>
      )}

      <Box
        sx={{
          position: 'relative',
          height: 32,
          borderRadius: 2,
          bgcolor: 'action.hover',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            width: `${fillWidth}%`,
            background: gradient,
            borderRadius: 2,
            transition: 'width 0.45s ease',
            ...(glow && { animation: `${quotaGlow} 1.8s ease-in-out infinite` }),
          }}
        />
        <Typography
          variant="body2"
          fontWeight={700}
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: fillWidth > 45 ? '#fff' : 'text.primary',
            textShadow: fillWidth > 45 ? '0 1px 2px rgba(0,0,0,0.35)' : 'none',
            zIndex: 1,
          }}
        >
          {percentLabel}
        </Typography>
      </Box>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 1.25, textAlign: 'center', fontWeight: 500 }}
      >
        {usedLabel} / {maxLabel}
      </Typography>

      {folderPath && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Chip
            label={folderPath}
            size="medium"
            variant="outlined"
            sx={{
              maxWidth: '100%',
              fontWeight: 600,
              borderColor: barAccent,
              bgcolor: `${barAccent}18`,
              color: barAccent,
              '& .MuiChip-label': { color: barAccent },
            }}
          />
        </Box>
      )}
    </Box>
  );
};
